import { ICellRendererParams, RowNode } from '@ag-grid-community/core';

import { FileBundleData } from 'src/clients/FilesClient';
import { FILE_TYPE } from 'src/constants';
import { BaseEntity, FileSettings } from 'src/constants/dataTypes';
import { FileNotification } from 'src/store/notifications/types';
import {
  SignatureEvent,
  SignaturePageComponent,
  SignaturePageImageStatus,
} from 'src/store/signaturePage/types';
import { UserProfile } from 'src/utils/UserUtils';

// File access levels for folder locking
export type FileAccessLevel = 'read_only' | 'read_write';

// Duplicate behavior for handling file name conflicts
export type DuplicateBehavior = 'replace' | 'append';

export enum FilesActionTypes {
  GET_FILES = 'GET_FILES',
  GET_FILES_SUCCESS = 'GET_FILES_SUCCESS',
  GET_FILES_ERROR = 'GET_FILES_ERROR',
  SAVE_UPLOADED_FILES = 'SAVE_UPLOADED_FILES',
  SAVE_UPLOADED_FILES_SUCCESS = 'SAVE_UPLOADED_FILES_SUCCESS',
  SAVE_UPLOADED_FILES_ERROR = 'SAVE_UPLOADED_FILES_ERROR',
  SELECT_FILES_CHANNEL = 'SELECT_FILES_CHANNEL',
  DELETE_FILES = 'DELETE_FILES',
  DELETE_FOLDER = 'DELETE_FOLDER',
  DOWNLOAD_FILE = 'DOWNLOAD_FILE',
  DOWNLOAD_FILE_SUCCESS = 'DOWNLOAD_FILE_SUCCESS',
  DOWNLOAD_FILE_ERROR = 'DOWNLOAD_FILE_ERROR',
  CLEAR_FILES = 'CLEAR_FILES',
  UPDATE_FILE_FIELDS_REQUEST = 'UPDATE_FILE_FIELDS_REQUEST',
  UPDATE_FILE_FIELDS_SUCCESS = 'UPDATE_FILE_FIELDS_SUCCESS',
  UPDATE_FILE_FIELDS_FAILURE = 'UPDATE_FILE_FIELDS_FAILURE',
  MOVE_FOLDER_REQUEST = 'MOVE_FOLDER_REQUEST',
  MOVE_FOLDER_SUCCESS = 'MOVE_FOLDER_SUCCESS',
  MOVE_FOLDER_FAILURE = 'MOVE_FOLDER_FAILURE',
  SET_FILE_CHANNELS = 'SET_FILE_CHANNELS',
  ADD_FILE_CHANNELS = 'ADD_FILE_CHANNELS',
  REMOVE_FILE_CHANNELS = 'REMOVE_FILE_CHANNELS',
  INSERT_FILES = 'INSERT_FILES',
  UPDATE_FILE_CHANNELS = 'UPDATE_FILE_CHANNELS',
  UPDATE_FILE_CHANNEL = 'UPDATE_FILE_CHANNEL',
  CANCEL_ESIGNATURE_REQUEST_SUCCESS = 'CANCEL_ESIGNATURE_REQUEST_SUCCESS',
  UPDATE_FILE_DRAGGING = 'UPDATE_FILE_DRAGGING',
  UPDATE_HOVERED_ROW_NODE = 'UPDATE_HOVERED_ROW_NODE',
  UPDATE_FILES_HELPER_ACTION = 'UPDATE_FILES_HELPER_ACTION',
  DELETE_FILE_CHANNEL = 'DELETE_FILE_CHANNEL',
  UNSELECT_CHANNEL = 'UNSELECT_CHANNEL',
  DUPLICATE_CONFLICT_DETECTED = 'DUPLICATE_CONFLICT_DETECTED',
  CLEAR_DUPLICATE_CONFLICT = 'CLEAR_DUPLICATE_CONFLICT',
}

export interface FileQueryParamProps {
  channelId?: string;
  selectedFilePath?: string;
  clientUserId?: string;
}

export interface FileCellRendererProps extends ICellRendererParams {
  channelId?: string;
  selectedFilePath?: string;
  isClient: boolean;
  fileSettings?: FileSettings;
}

export interface AddFileChannelsActionOptions {
  selectFirstChannel: boolean;
}

export interface UnselectFileChannelOptions {
  navigationPath: string;
  navigate: (path: string) => void;
  openSecondarySidebar: boolean;
}

export enum ClientTypes {
  CLIENT_COMPANY = 'CLIENT_COMPANY',
  CLIENT_USER = 'CLIENT_USER',
  OWNER = 'owner',
}

export interface SelectedClient {
  clientType:
    | ClientTypes.CLIENT_COMPANY
    | ClientTypes.CLIENT_USER
    | ClientTypes.OWNER
    | ''
    | undefined;
  clientId: string;
}

export interface FileChannelFields {
  name?: string;
  userId?: string;
  companyID?: string;
  lastNotification?: FileNotification;
  channelType?: 'multi';
  singleClientId?: string;
  filesCount?: number;
}

export interface FilesTableActionInput {
  actionType: string;
  fileData: FilesTableData;
  fileLinkURLs?: string[];
  fileBundleData?: FileBundleData[];
  bundleName?: string;
}

export interface FileChannelAdditionalFields {
  memberIDs: Record<string, boolean>;
}

export interface FileChannel extends BaseEntity {
  fields: FileChannelFields;
  additionalFields?: FileChannelAdditionalFields;
  rootClientPermissions?: FileAccessLevel;
}

export type SignatureStatusTypes =
  | 'pending'
  | 'open'
  | 'sent'
  | 'signed'
  | 'done';
export interface Fields {
  fileKey: string;
  starred: boolean;
  tags?: Array<string>;
  fileName?: string;
  path?: string;
  fileUrl?: string;
  status?: string;
  pageImageKeys?: Array<string>;
  pageImageStatus: SignaturePageImageStatus;
  esignStatus?: SignatureStatusTypes;
  components?: Array<SignaturePageComponent>;
  signatureEvents?: Array<SignatureEvent>;
  lastEsigModifierIdentityID?: string;
}

export interface FileResponse extends BaseEntity {
  fileUrl: string;
  fields: Fields;
  creatorId: string;
  clientPermissions?: FileAccessLevel;
}

export interface BatchError {
  code?: string;
  type?: string;
  message?: string;
}

export interface CreateFilesResponse {
  createdItems: Array<FileResponse>;
  failedIds: Array<string>;
  succeededIds: Array<string>;
  unauthorizedIds: Array<string>;
  errors?: Array<BatchError>;
}

export interface DownloadFileModel {
  id?: string;
  fileUrl: string;
  fileKey: string;
  fileName: string;
  emitAuditEvent?: boolean;
  channelId?: string;
  path?: string;
}

export interface FileSaveModel {
  key: string;
  path: string;
  url?: string;
}

export interface DuplicateConflictState {
  fileName: string;
  additionalConflictCount?: number; // Number of additional files with conflicts (besides the first one)
  files?: FileSaveModel[]; // Optional - only used for 'create' operation
  channelId: string;
  operation: 'create' | 'rename' | 'move';
  // For rename operations
  originalFileData?: FilesTableFormData;
  existingPaths?: string[];
  // For move operations
  moveParams?: {
    targetPath: string;
    sourcePath: string;
    fileType: string;
    fileKey: string;
    entityName: string;
  };
}

export interface FileFieldsUpdateSuccessPayload {
  id: string;
  clientPermissions?: FileAccessLevel;
  fields: {
    status?: string;
    fileName: string;
    fileKey: string;
    starred: boolean;
    path: string;
    esignStatus?: string;
    lastEsigModifierIdentityID?: string;
    components?: Array<SignaturePageComponent>;
  };
}

export interface FilesState {
  items: FileResponse[];
  selectedChannel?: FileChannel;
  loading: boolean;
  loaded: boolean;
  saving: boolean;
  saved: boolean;
  downloading: boolean;
  error?: string;
  renaming?: boolean;
  moving: boolean;
  channels: FileChannel[];
  channelsLoaded: boolean;
  fileDragging: boolean;
  hoveredRowNode: RowNode | null;
  helperActions: Record<any, string | null>;
  duplicateConflict: DuplicateConflictState | null;
}

export interface PreprocessedFilesTableData {
  creator: UserProfile;
  fileId: string;
  channelId?: string;
  fileKey: string;
  fileName: string;
  identityId: string;
  createdDate: string;
  path: string[];
  filePath: string[];
  fileUrl: string;
  type: ValueOf<typeof FILE_TYPE> | '';
  pageImageKeys: string[] | undefined;
  signatureStatus: SignatureStatusTypes | undefined;
  lastEsigModifierIdentityID: string;
  clientPermissions?: FileAccessLevel;
  parentClientPermissions?: FileAccessLevel;
}

export interface FilesTableData extends PreprocessedFilesTableData {
  type: ValueOf<typeof FILE_TYPE>;
}

export interface FilesTableFormData extends FilesTableData {
  fields: Partial<FilesTableData>;
}

export interface CopyFilesTableFormData extends FilesTableFormData {
  fileChannelIds: Array<string>;
}

export interface FolderForm {
  name: string;
}

interface GetFiles {
  type: typeof FilesActionTypes.GET_FILES;
}

interface GetFilesSuccess {
  type: typeof FilesActionTypes.GET_FILES_SUCCESS;
  payload: FileResponse[];
}

interface GetFilesError {
  type: typeof FilesActionTypes.GET_FILES_ERROR;
  error: string;
}

interface SendFiles {
  type: typeof FilesActionTypes.SAVE_UPLOADED_FILES;
}

interface SendFilesSuccess {
  type: typeof FilesActionTypes.SAVE_UPLOADED_FILES_SUCCESS;
  payload: Array<FileResponse>;
  duplicateBehavior?: DuplicateBehavior;
  // Paths of folders being replaced (used to remove old nested files from state)
  replacedFolderPaths?: string[];
  // Paths of files being replaced (used to remove old files from state)
  replacedFilePaths?: Array<{ path: string; fileName: string }>;
}

interface SendFilesError {
  type: typeof FilesActionTypes.SAVE_UPLOADED_FILES_ERROR;
  error: string;
}

interface SelectChannel {
  type: typeof FilesActionTypes.SELECT_FILES_CHANNEL;
  payload?: FileChannel;
}

interface DeleteFiles {
  type: typeof FilesActionTypes.DELETE_FILES;
  payload: string[];
}

interface DeleteFolder {
  type: typeof FilesActionTypes.DELETE_FOLDER;
  payload: string;
}

interface ClearFiles {
  type: typeof FilesActionTypes.CLEAR_FILES;
}

interface DownloadFile {
  type: typeof FilesActionTypes.DOWNLOAD_FILE;
}

interface DownloadFileSuccess {
  type: typeof FilesActionTypes.DOWNLOAD_FILE_SUCCESS;
}

interface DownloadFileError {
  type: typeof FilesActionTypes.DOWNLOAD_FILE_ERROR;
  error: string;
}

interface UpdateFileFields {
  type: typeof FilesActionTypes.UPDATE_FILE_FIELDS_REQUEST;
}

interface UpdateFileFieldsSuccess {
  type: typeof FilesActionTypes.UPDATE_FILE_FIELDS_SUCCESS;
  successIds: string[];
  updatedFiles: Array<FileFieldsUpdateSuccessPayload>;
}

interface UpdateFileFieldsError {
  type: typeof FilesActionTypes.UPDATE_FILE_FIELDS_FAILURE;
  error: string;
}

interface MoveFolderStart {
  type: typeof FilesActionTypes.MOVE_FOLDER_REQUEST;
}

interface MoveFolderSuccess {
  type: typeof FilesActionTypes.MOVE_FOLDER_SUCCESS;
  payload: FileResponse[];
}

interface MoveFolderError {
  type: typeof FilesActionTypes.MOVE_FOLDER_FAILURE;
  error: string;
}

interface SetFileChannelsActionType {
  type: typeof FilesActionTypes.SET_FILE_CHANNELS;
  channels: FileChannel[];
}

export interface AddFileChannelsActionType {
  type: typeof FilesActionTypes.ADD_FILE_CHANNELS;
  channels: FileChannel[];
  addFileChannelOptions?: AddFileChannelsActionOptions;
}

export interface DeleteFileChannelActionType {
  type: typeof FilesActionTypes.DELETE_FILE_CHANNEL;
  channelId: string;
}

export interface UpdateFileChannelsActionType {
  type: typeof FilesActionTypes.UPDATE_FILE_CHANNELS;
  updatedChannels: FileChannel[];
}

export interface UpdateFileChannelActionType {
  type: typeof FilesActionTypes.UPDATE_FILE_CHANNEL;
  channelId: string;
  updates: Partial<FileChannel>;
}

export interface RemoveFileChannelsActionType {
  type: typeof FilesActionTypes.REMOVE_FILE_CHANNELS;
  channels: FileChannel[];
}

export interface CancelESignatureRequestSuccess {
  type: typeof FilesActionTypes.CANCEL_ESIGNATURE_REQUEST_SUCCESS;
  payload: FileResponse;
}

export interface UnselectChannel {
  type: typeof FilesActionTypes.UNSELECT_CHANNEL;
}

interface InsertFilesActionType {
  type: typeof FilesActionTypes.INSERT_FILES;
  payload: FileResponse[];
}
interface UpdateFileDraggingActionType {
  type: typeof FilesActionTypes.UPDATE_FILE_DRAGGING;
  payload: boolean;
}
interface UpdateHoveredRowNodeActionType {
  type: typeof FilesActionTypes.UPDATE_HOVERED_ROW_NODE;
  payload: RowNode;
}

interface UpdateFilesHelperAction {
  type: typeof FilesActionTypes.UPDATE_FILES_HELPER_ACTION;
  payload: string | null;
}

export interface DuplicateConflictDetectedAction {
  type: typeof FilesActionTypes.DUPLICATE_CONFLICT_DETECTED;
  payload: DuplicateConflictState;
}

export interface ClearDuplicateConflictAction {
  type: typeof FilesActionTypes.CLEAR_DUPLICATE_CONFLICT;
}

export type FilesActions =
  | GetFiles
  | GetFilesSuccess
  | GetFilesError
  | SendFiles
  | SendFilesSuccess
  | SendFilesError
  | SelectChannel
  | DeleteFiles
  | ClearFiles
  | DownloadFile
  | DownloadFileSuccess
  | DownloadFileError
  | UpdateFileFields
  | UpdateFileFieldsSuccess
  | UpdateFileFieldsError
  | MoveFolderStart
  | MoveFolderSuccess
  | MoveFolderError
  | SetFileChannelsActionType
  | AddFileChannelsActionType
  | RemoveFileChannelsActionType
  | InsertFilesActionType
  | UpdateFileChannelsActionType
  | UpdateFileChannelActionType
  | CancelESignatureRequestSuccess
  | UpdateFileDraggingActionType
  | UpdateHoveredRowNodeActionType
  | UpdateFilesHelperAction
  | DeleteFileChannelActionType
  | UnselectChannel
  | DeleteFolder
  | DuplicateConflictDetectedAction
  | ClearDuplicateConflictAction;
